import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getGroupById, getActiveMembers } from '../services/groupService';
import { getActivePeriod } from '../services/periodService';
import { getUsersByIds } from '../services/userService';
import { createMatch } from '../services/matchService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {
  COLLECTIONS,
  GROUP_MEMBERS_FIELDS,
  RATINGS_FIELDS,
  MATCH_TYPES,
  SET_FIELDS,
  SET_FIELD_VALUES,
  MATCH_TEAM_FIELDS,
  GAME_FORMATS,
  LEGACY_TYPE_TO_GAME_FORMAT,
  RATING_IMPACT,
  MATCHES_FIELDS,
  PERIODS_FIELDS,
  RATING_MODES,
  RATING_PRESETS,
  ELO_CONFIG,
} from '../domain/dataModel';
import { calculateEloUpdates } from '../utils/eloCalculator';

const today = new Date();
today.setHours(0, 0, 0, 0);
dayjs.locale('it');

function generateCalendarDays(month) {
  const startOfMonth = month.startOf('month');
  const startDow = (startOfMonth.day() + 6) % 7;
  const days = [];

  for (let i = startDow - 1; i >= 0; i--) {
    days.push(startOfMonth.subtract(i + 1, 'day'));
  }

  const daysInMonth = month.daysInMonth();
  for (let i = 0; i < daysInMonth; i++) {
    days.push(startOfMonth.add(i, 'day'));
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(startOfMonth.add(daysInMonth - 1 + i, 'day'));
  }

  return days;
}

function IconArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconShuffle({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 3h5v5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 20L21 3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 16v5h-5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 15l6 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 4l5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrophy({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M8 21h8" strokeLinecap="round" />
      <path d="M12 17v4" strokeLinecap="round" />
      <path d="M7 4h10v6a5 5 0 0 1-10 0V4Z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 6h2a2 2 0 0 1 0 4h-2" strokeLinecap="round" />
      <path d="M7 6H5a2 2 0 1 0 0 4h2" strokeLinecap="round" />
    </svg>
  );
}

function getInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function getHashColor(seed = '') {
  const palette = ['bg-brand-royal/30 text-brand-ice', 'bg-brand-sky/25 text-brand-ice', 'bg-gold/20 text-gold', 'bg-brand-navy/60 text-brand-ice'];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash << 5) - hash + seed.charCodeAt(i);
  return palette[Math.abs(hash) % palette.length];
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildKingPairs(order) {
  if (order.length !== 4) return [];
  const [A, B, C, D] = order;
  return [
    { team1: [A, B], team2: [C, D] },
    { team1: [A, C], team2: [B, D] },
    { team1: [A, D], team2: [B, C] },
  ];
}

/** Per ogni campo (ordine 4 giocatori + 3 set): calcola set vinti e game vinti per giocatore, ordina per set decrescente poi game decrescente. Ritorna [{ playerId, setWins, gamesWon }] 1°..4°. */
function computeFieldStandings(order, setScores) {
  if (!order || order.length !== 4 || !setScores || setScores.length < 3) return [];
  const pairs = buildKingPairs(order);
  const stats = { [order[0]]: { setWins: 0, gamesWon: 0 }, [order[1]]: { setWins: 0, gamesWon: 0 }, [order[2]]: { setWins: 0, gamesWon: 0 }, [order[3]]: { setWins: 0, gamesWon: 0 } };
  for (let i = 0; i < 3; i++) {
    const p = pairs[i];
    const s = setScores[i] || { score1: 0, score2: 0 };
    const score1 = Number(s.score1) || 0;
    const score2 = Number(s.score2) || 0;
    const team1Wins = score1 > score2;
    p.team1.forEach((pid) => {
      stats[pid].setWins += team1Wins ? 1 : 0;
      stats[pid].gamesWon += score1;
    });
    p.team2.forEach((pid) => {
      stats[pid].setWins += team1Wins ? 0 : 1;
      stats[pid].gamesWon += score2;
    });
  }
  return order
    .map((playerId) => ({ playerId, setWins: stats[playerId].setWins, gamesWon: stats[playerId].gamesWon }))
    .sort((a, b) => b.setWins - a.setWins || b.gamesWon - a.gamesWon);
}

const TYPE_CONFIG = {
  [MATCH_TYPES.CLASSIC]: {
    label: 'Classica',
    description: 'Partita a coppie fisse. Scegli le due coppie e il numero di set.',
    players: 4,
  },
  [MATCH_TYPES.KING]: {
    label: 'King',
    description: '3 set con rotazione delle coppie. Ordine giocatori casuale (A,B,C,D): Set 1 A+B vs C+D, Set 2 A+C vs B+D, Set 3 A+D vs B+C.',
    players: 4,
  },
  [MATCH_TYPES.SUPER_KING]: {
    label: 'Super King',
    description: '8 giocatori, due campi (Gold e Silver). Serpentina per rating. King per campo poi finali incrociate.',
    players: 8,
  },
};

export default function AddMatch() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [group, setGroup] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [members, setMembers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [matchType, setMatchType] = useState(null);
  const [playedAt, setPlayedAt] = useState(dayjs());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  const [classicNumSets, setClassicNumSets] = useState(1);
  const [classicPairA, setClassicPairA] = useState([]);
  const [classicPairB, setClassicPairB] = useState([]);
  const [classicSetScores, setClassicSetScores] = useState([{ score1: 0, score2: 0 }]);

  const [kingOrder, setKingOrder] = useState([]);
  const [kingSetScores, setKingSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);

  const [superGoldField, setSuperGoldField] = useState([]);
  const [superSilverField, setSuperSilverField] = useState([]);
  const [superGoldOrder, setSuperGoldOrder] = useState([]);
  const [superSilverOrder, setSuperSilverOrder] = useState([]);
  const [superGoldSetScores, setSuperGoldSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superSilverSetScores, setSuperSilverSetScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superGoldFinalScores, setSuperGoldFinalScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);
  const [superSilverFinalScores, setSuperSilverFinalScores] = useState([{ score1: 0, score2: 0 }, { score1: 0, score2: 0 }, { score1: 0, score2: 0 }]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [ratingImpact, setRatingImpact] = useState(RATING_IMPACT.OFFICIAL);
  const [matchResultCalculated, setMatchResultCalculated] = useState(false);
  const [eloPreview, setEloPreview] = useState(null);
  const [autoBalancePulse, setAutoBalancePulse] = useState(0);

  const requiredPlayers = matchType ? TYPE_CONFIG[matchType]?.players ?? 4 : 0;
  useEffect(() => {
    if (!user?.uid) return;
    if (!groupId) {
      setLoading(false);
      setError('ID gruppo mancante. Apri la pagina dal dettaglio del gruppo.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [groupData, active, membersList] = await Promise.all([
          getGroupById(groupId),
          getActivePeriod(groupId),
          getActiveMembers(groupId),
        ]);
        if (cancelled) return;
        if (!groupData) {
          setError('Gruppo non trovato');
          setLoading(false);
          return;
        }
        setGroup(groupData);
        setActivePeriod(active);
        setMembers(membersList);
        if (active) {
          const ratingsSnap = await getDocs(query(collection(db, COLLECTIONS.RATINGS), where(RATINGS_FIELDS.GROUP_ID, '==', groupId), where(RATINGS_FIELDS.PERIOD_ID, '==', active.id), orderBy(RATINGS_FIELDS.RATING, 'desc')));
          setRatings(ratingsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        setError(err.message || 'Errore di caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, user?.uid]);

  useEffect(() => {
    const ids = [...new Set(members.map((m) => m[GROUP_MEMBERS_FIELDS.USER_ID]).filter(Boolean))];
    if (ids.length === 0) {
      setUsersMap({});
      return;
    }
    getUsersByIds(ids).then((usersData) => {
      const map = {};
      ids.forEach((uid) => {
        const u = usersData[uid];
        map[uid] = u ? (u.displayName || u.email || uid) : uid;
      });
      setUsersMap(map);
    });
  }, [members]);

  const getPlayerId = (m) => (m[GROUP_MEMBERS_FIELDS.IS_GUEST] ? m.id : m[GROUP_MEMBERS_FIELDS.USER_ID]);
  const getDisplayNameForPlayer = (playerId) => {
    const m = members.find((x) => x.id === playerId || x[GROUP_MEMBERS_FIELDS.USER_ID] === playerId);
    if (!m) return String(playerId);
    if (m[GROUP_MEMBERS_FIELDS.IS_GUEST]) return m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite';
    return usersMap[m[GROUP_MEMBERS_FIELDS.USER_ID]] || m[GROUP_MEMBERS_FIELDS.USER_ID];
  };

  const togglePlayer = (playerId) => {
    setSelectedPlayers((prev) => {
      if (prev.includes(playerId)) return prev.filter((id) => id !== playerId);
      if (prev.length >= requiredPlayers) return prev;
      return [...prev, playerId];
    });
  };

  const canAdvanceStep1 = !!matchType && playedAt && playedAt.isValid() && !playedAt.isAfter(dayjs(), 'day');
  const canAdvanceStep2 = matchType === MATCH_TYPES.CLASSIC
    ? (classicPairA.length === 2 && classicPairB.length === 2)
    : selectedPlayers.length === requiredPlayers;

  const superKingGoldStandings = useMemo(
    () => computeFieldStandings(superGoldOrder, superGoldSetScores),
    [superGoldOrder, superGoldSetScores]
  );
  const superKingSilverStandings = useMemo(
    () => computeFieldStandings(superSilverOrder, superSilverSetScores),
    [superSilverOrder, superSilverSetScores]
  );

  useEffect(() => {
    if (matchType !== MATCH_TYPES.KING || selectedPlayers.length !== 4) return;
    if (kingOrder.length === 0) setKingOrder(shuffle(selectedPlayers));
  }, [matchType, selectedPlayers, kingOrder.length]);

  useEffect(() => {
    if (matchType !== MATCH_TYPES.SUPER_KING || selectedPlayers.length !== 8) return;
    if (superGoldField.length === 0 && ratings.length > 0) {
      const byRating = [...selectedPlayers].sort((a, b) => {
        const ra = ratings.find((r) => r[RATINGS_FIELDS.USER_ID] === a)?.[RATINGS_FIELDS.RATING] ?? 0;
        const rb = ratings.find((r) => r[RATINGS_FIELDS.USER_ID] === b)?.[RATINGS_FIELDS.RATING] ?? 0;
        return rb - ra;
      });
      setSuperGoldField([byRating[0], byRating[3], byRating[4], byRating[7]]);
      setSuperSilverField([byRating[1], byRating[2], byRating[5], byRating[6]]);
    }
  }, [matchType, selectedPlayers, ratings, superGoldField.length]);

  useEffect(() => {
    if (superGoldField.length === 4 && superGoldOrder.length === 0) setSuperGoldOrder(shuffle(superGoldField));
    if (superSilverField.length === 4 && superSilverOrder.length === 0) setSuperSilverOrder(shuffle(superSilverField));
  }, [superGoldField, superSilverField, superGoldOrder.length, superSilverOrder.length]);

  const regenerateKing = () => setKingOrder(shuffle(selectedPlayers));
  const regenerateSuperGold = () => setSuperGoldOrder(shuffle(superGoldField));
  const regenerateSuperSilver = () => setSuperSilverOrder(shuffle(superSilverField));

  const moveBetweenFields = (fromGold, index) => {
    if (fromGold) {
      const id = superGoldField[index];
      setSuperGoldField((g) => g.filter((_, i) => i !== index));
      setSuperSilverField((s) => [...s, id]);
      setSuperGoldOrder([]);
      setSuperSilverOrder([]);
    } else {
      const id = superSilverField[index];
      setSuperSilverField((s) => s.filter((_, i) => i !== index));
      setSuperGoldField((g) => [...g, id]);
      setSuperGoldOrder([]);
      setSuperSilverOrder([]);
    }
  };

  useEffect(() => {
    if (matchType !== MATCH_TYPES.CLASSIC) return;
    setClassicSetScores((prev) => {
      const next = Array.from({ length: classicNumSets }, (_, i) => prev[i] ?? { score1: 0, score2: 0 });
      return next.length === prev.length ? prev : next;
    });
  }, [classicNumSets, matchType]);

  const buildSetsPayload = () => {
    const sets = [];
    if (matchType === MATCH_TYPES.CLASSIC) {
      for (let i = 0; i < classicNumSets; i++) {
        const s = classicSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: classicPairA[0], [MATCH_TEAM_FIELDS.PLAYER2]: classicPairA[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: classicPairB[0], [MATCH_TEAM_FIELDS.PLAYER2]: classicPairB[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      }
    } else if (matchType === MATCH_TYPES.KING) {
      const pairs = buildKingPairs(kingOrder);
      pairs.forEach((p, i) => {
        const s = kingSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
    } else if (matchType === MATCH_TYPES.SUPER_KING) {
      const goldPairs = buildKingPairs(superGoldOrder);
      goldPairs.forEach((p, i) => {
        const s = superGoldSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.FIELD]: SET_FIELD_VALUES.GOLD_KING,
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
      const silverPairs = buildKingPairs(superSilverOrder);
      silverPairs.forEach((p, i) => {
        const s = superSilverSetScores[i] || { score1: 0, score2: 0 };
        sets.push({
          [SET_FIELDS.FIELD]: SET_FIELD_VALUES.SILVER_KING,
          [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team1[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team1[1] },
          [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: p.team2[0], [MATCH_TEAM_FIELDS.PLAYER2]: p.team2[1] },
          [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
          [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
        });
      });
      const gSt = computeFieldStandings(superGoldOrder, superGoldSetScores);
      const sSt = computeFieldStandings(superSilverOrder, superSilverSetScores);
      if (gSt.length >= 4 && sSt.length >= 4) {
        for (let i = 0; i < 3; i++) {
          const s = superGoldFinalScores[i] || { score1: 0, score2: 0 };
          sets.push({
            [SET_FIELDS.FIELD]: SET_FIELD_VALUES.GOLD_FINAL,
            [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[0].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[0].playerId },
            [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[1].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[1].playerId },
            [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
            [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
          });
        }
        for (let i = 0; i < 3; i++) {
          const s = superSilverFinalScores[i] || { score1: 0, score2: 0 };
          sets.push({
            [SET_FIELDS.FIELD]: SET_FIELD_VALUES.SILVER_FINAL,
            [SET_FIELDS.TEAM1]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[2].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[2].playerId },
            [SET_FIELDS.TEAM2]: { [MATCH_TEAM_FIELDS.PLAYER1]: gSt[3].playerId, [MATCH_TEAM_FIELDS.PLAYER2]: sSt[3].playerId },
            [SET_FIELDS.SCORE1]: Number(s.score1) || 0,
            [SET_FIELDS.SCORE2]: Number(s.score2) || 0,
          });
        }
      }
    }
    return sets;
  };

  const handleCalculateResult = () => {
    const sets = buildSetsPayload();
    if (sets.length === 0) return;

    let setsWonA = 0; let setsWonB = 0; let gamesWonA = 0; let gamesWonB = 0;
    sets.forEach((s) => {
      const s1 = Number(s[SET_FIELDS.SCORE1]) || 0;
      const s2 = Number(s[SET_FIELDS.SCORE2]) || 0;
      if (s1 > s2) setsWonA += 1;
      else if (s2 > s1) setsWonB += 1;
      gamesWonA += s1;
      gamesWonB += s2;
    });
    let winner = 'DRAW';
    if (setsWonA > setsWonB) winner = 'A';
    else if (setsWonB > setsWonA) winner = 'B';
    else if (gamesWonA > gamesWonB) winner = 'A';
    else if (gamesWonB > gamesWonA) winner = 'B';

    let deltaA = null; let deltaB = null;
    if (ratingImpact === RATING_IMPACT.OFFICIAL && activePeriod && classicPairA.length === 2 && classicPairB.length === 2) {
      try {
        const matchForElo = {
          [MATCHES_FIELDS.GAME_FORMAT]: GAME_FORMATS.MATCH,
          [MATCHES_FIELDS.SETS]: sets,
        };
        const periodForElo = {
          [PERIODS_FIELDS.RATING_MODE]: activePeriod[PERIODS_FIELDS.RATING_MODE] || RATING_MODES.RESULT_ONLY,
          [PERIODS_FIELDS.RATING_PRESET]: activePeriod[PERIODS_FIELDS.RATING_PRESET] || RATING_PRESETS.CLASSIC,
        };
        const playerRatingsMap = new Map();
        [...classicPairA, ...classicPairB].forEach((pid) => {
          const r = ratings.find((x) => x[RATINGS_FIELDS.USER_ID] === pid || x[RATINGS_FIELDS.MEMBER_ID] === pid);
          playerRatingsMap.set(pid, {
            rating: r?.[RATINGS_FIELDS.RATING] ?? ELO_CONFIG.RATING_BASE,
            matchesPlayed: r?.[RATINGS_FIELDS.MATCHES_PLAYED] ?? 0,
          });
        });
        const results = calculateEloUpdates(matchForElo, periodForElo, playerRatingsMap);
        const resA = results.find((r) => r.memberId === classicPairA[0]);
        const resB = results.find((r) => r.memberId === classicPairB[0]);
        deltaA = resA?.delta ?? null;
        deltaB = resB?.delta ?? null;
      } catch (_) {
        // In caso di errore del preview ELO, mostriamo comunque il risultato match.
      }
    }

    setEloPreview({ winner, setsWonA, setsWonB, gamesWonA, gamesWonB, deltaA, deltaB });
    setMatchResultCalculated(true);
  };

  const removeUndefined = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined)
    );
  };

  const handleSubmit = async () => {
    if (!groupId || !activePeriod || !user?.uid) return;
    setSubmitLoading(true);
    setError(null);
    try {
      const sets = buildSetsPayload();
      const now = new Date();
      const cleanedSets = sets.map((s) =>
        typeof s === 'object' && s !== null ? removeUndefined(s) : s
      );
      const matchPayload = {
        type: matchType,
        gameFormat: LEGACY_TYPE_TO_GAME_FORMAT[matchType] || GAME_FORMATS.MATCH,
        ratingImpact,
        sets: cleanedSets,
        playedAt: playedAt.toDate(),
        approvals: [{ userId: user.uid, approvedAt: now }],
      };
      await createMatch(groupId, activePeriod.id, matchPayload, user.uid);
      setSubmitSuccess(true);
      setTimeout(() => navigate(`/groups/${groupId}`), 1500);
    } catch (err) {
      setError(err.message || 'Errore nel salvataggio');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    setMatchResultCalculated(false);
    setEloPreview(null);
  }, [classicSetScores, classicPairA, classicPairB]);

  useEffect(() => {
    if (matchType !== MATCH_TYPES.CLASSIC) return;
    setSelectedPlayers([...classicPairA, ...classicPairB]);
  }, [classicPairA, classicPairB, matchType]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate('/')} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error || 'Gruppo non trovato'}
        </div>
      </div>
    );
  }

  if (!activePeriod) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <div className="rounded-btn border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-gold">
          Nessun periodo attivo. Impossibile aggiungere una partita.
        </div>
      </div>
    );
  }

  const steps = matchType === MATCH_TYPES.CLASSIC
    ? ['Tipo partita', 'Giocatori e Squadre', 'Punteggi']
    : ['Tipo partita', 'Giocatori', 'Formazione squadre', 'Punteggi'];
  const canGoToClassicScores = matchType !== MATCH_TYPES.CLASSIC || (classicPairA.length === 2 && classicPairB.length === 2);
  const playerRatingById = (pid) => {
    const r = ratings.find((x) => x[RATINGS_FIELDS.USER_ID] === pid || x[RATINGS_FIELDS.MEMBER_ID] === pid);
    return r?.[RATINGS_FIELDS.RATING] ?? ELO_CONFIG.RATING_BASE;
  };
  const avgA = classicPairA.length === 2 ? Math.round((playerRatingById(classicPairA[0]) + playerRatingById(classicPairA[1])) / 2) : 0;
  const avgB = classicPairB.length === 2 ? Math.round((playerRatingById(classicPairB[0]) + playerRatingById(classicPairB[1])) / 2) : 0;
  const classicSelected = [...classicPairA, ...classicPairB];
  const classicReady = classicPairA.length === 2 && classicPairB.length === 2;
  const classicGap = classicReady ? Math.abs(avgA - avgB) : 0;
  const classicBalanceState = classicGap <= 50 ? 'balanced' : classicGap <= 150 ? 'slightly_unbalanced' : 'unbalanced';
  const classicHint = classicPairA.length > 0 && classicPairB.length > 0;
  const classicSubtitle = classicSelected.length === 0
    ? 'Seleziona i giocatori per la Squadra A'
    : classicPairA.length === 2 && classicPairB.length < 2
      ? 'Ora seleziona i giocatori per la Squadra B'
      : classicSelected.length === 4
        ? '4/4 selezionati'
        : `Seleziona giocatori ${classicSelected.length}/4`;

  const getClassicTeam = (playerId) => {
    if (classicPairA.includes(playerId)) return 'A';
    if (classicPairB.includes(playerId)) return 'B';
    return null;
  };

  const togglePlayerForMatch = (playerId) => {
    const inA = classicPairA.includes(playerId);
    const inB = classicPairB.includes(playerId);
    if (inA) {
      setClassicPairA((prev) => prev.filter((id) => id !== playerId));
      return;
    }
    if (inB) {
      setClassicPairB((prev) => prev.filter((id) => id !== playerId));
      return;
    }
    if (classicPairA.length < 2) {
      setClassicPairA((prev) => [...prev, playerId]);
    } else if (classicPairB.length < 2) {
      setClassicPairB((prev) => [...prev, playerId]);
    }
  };

  const handleSwitchPlayer = (playerId) => {
    const indexInA = classicPairA.indexOf(playerId);
    const indexInB = classicPairB.indexOf(playerId);

    if (indexInA !== -1 && classicPairB.length < 2) {
      setClassicPairA((prev) => prev.filter((id) => id !== playerId));
      setClassicPairB((prev) => [...prev, playerId]);
    } else if (indexInB !== -1 && classicPairA.length < 2) {
      setClassicPairB((prev) => prev.filter((id) => id !== playerId));
      setClassicPairA((prev) => [...prev, playerId]);
    } else if (indexInA !== -1 && classicPairB.length === 2) {
      const targetId = classicPairB[indexInA];
      setClassicPairA((prev) => prev.map((id) => (id === playerId ? targetId : id)));
      setClassicPairB((prev) => prev.map((id) => (id === targetId ? playerId : id)));
    } else if (indexInB !== -1 && classicPairA.length === 2) {
      const targetId = classicPairA[indexInB];
      setClassicPairB((prev) => prev.map((id) => (id === playerId ? targetId : id)));
      setClassicPairA((prev) => prev.map((id) => (id === targetId ? playerId : id)));
    }
  };

  const autoBalanceClassicTeams = () => {
    if (!classicReady) return;
    const [a, b, c, d] = classicSelected;
    const combos = [
      { teamA: [a, b], teamB: [c, d] },
      { teamA: [a, c], teamB: [b, d] },
      { teamA: [a, d], teamB: [b, c] },
    ];
    let best = combos[0];
    let bestGap = Number.POSITIVE_INFINITY;
    combos.forEach((combo) => {
      const comboAvgA = Math.round((playerRatingById(combo.teamA[0]) + playerRatingById(combo.teamA[1])) / 2);
      const comboAvgB = Math.round((playerRatingById(combo.teamB[0]) + playerRatingById(combo.teamB[1])) / 2);
      const comboGap = Math.abs(comboAvgA - comboAvgB);
      if (comboGap < bestGap) {
        bestGap = comboGap;
        best = combo;
      }
    });
    setClassicPairA(best.teamA);
    setClassicPairB(best.teamB);
    setAutoBalancePulse((x) => x + 1);
  };
  const QUICK_SCORES = [
    { s1: 6, s2: 0 },
    { s1: 6, s2: 1 },
    { s1: 6, s2: 2 },
    { s1: 6, s2: 3 },
    { s1: 6, s2: 4 },
    { s1: 6, s2: 5 },
  ];
  const applyQuickScore = (score1, score2) => {
    setClassicSetScores((prev) => {
      const next = [...prev];
      const lastIndex = Math.max(0, classicNumSets - 1);
      next[lastIndex] = { ...(next[lastIndex] || {}), score1, score2 };
      return next;
    });
  };
  const handleDuplicateLastSet = () => {
    const lastScore = classicSetScores[classicSetScores.length - 1] || { score1: 0, score2: 0 };
    setClassicNumSets((n) => n + 1);
    setClassicSetScores((prev) => [...prev, { ...lastScore }]);
  };

  return (
    <div className="space-y-5">
      <div className="mb-2 flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(`/groups/${groupId}`)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <h1 className="font-display text-2xl font-bold text-brand-ice">Aggiungi partita</h1>
      </div>
      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}
      {submitSuccess && (
        <div className="rounded-btn border border-win/60 bg-win/15 px-4 py-3 text-sm text-win">
          Partita inviata per approvazione.
        </div>
      )}

      <div className="mb-6 flex items-center gap-1 overflow-x-auto">
        {steps.map((label, idx) => (
          <React.Fragment key={label}>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${idx < activeStep ? 'bg-win text-white' : idx === activeStep ? 'bg-brand-royal text-white' : 'bg-surface-muted text-brand-ice/40'}`}>
                {idx < activeStep ? '✓' : idx + 1}
              </div>
              <span className={`hidden text-xs font-medium sm:block ${idx === activeStep ? 'text-brand-ice' : 'text-brand-ice/40'}`}>{label}</span>
            </div>
            {idx < steps.length - 1 && <div className={`h-px min-w-4 flex-1 ${idx < activeStep ? 'bg-win' : 'bg-surface-muted'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {activeStep === 0 && (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">Data partita</p>
          </div>
          <div className="space-y-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setCalendarOpen((o) => !o)}
                className="flex w-full items-center gap-4 rounded-card border border-surface-muted bg-surface-card px-4 py-4 text-left transition-colors hover:border-brand-royal/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-btn bg-brand-royal/20">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 text-brand-royal">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-lg font-bold text-brand-ice">
                    {playedAt ? playedAt.format('DD/MM/YYYY') : 'Seleziona data'}
                  </p>
                  <p className="text-sm text-brand-ice/50">
                    {playedAt ? playedAt.format('dddd') : ''}
                  </p>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 shrink-0 text-brand-ice/30">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </button>
              {calendarOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setCalendarOpen(false)}
                  />
                  <div className="absolute left-0 top-full z-50 mt-1 w-full max-w-sm rounded-card border border-surface-muted bg-surface-card p-4 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setCalendarMonth((m) => m.subtract(1, 'month'))}
                        className="rounded p-1 text-brand-ice/60 transition-colors hover:text-brand-ice"
                      >
                        ‹
                      </button>
                      <span className="text-sm font-semibold capitalize text-brand-ice">
                        {calendarMonth.format('MMMM YYYY')}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCalendarMonth((m) => m.add(1, 'month'))}
                        className="rounded p-1 text-brand-ice/60 transition-colors hover:text-brand-ice"
                      >
                        ›
                      </button>
                    </div>

                    <div className="mb-1 grid grid-cols-7 text-center">
                      {['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'].map((d) => (
                        <span key={d} className="text-xs font-medium text-brand-ice/40">{d}</span>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 text-center">
                      {generateCalendarDays(calendarMonth).map((day, i) => {
                        const isCurrentMonth = day.month() === calendarMonth.month();
                        const isSelected = playedAt && day.isSame(playedAt, 'day');
                        const isToday = day.isSame(dayjs(), 'day');
                        const isFuture = day.isAfter(dayjs(), 'day');
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={isFuture || !isCurrentMonth}
                            onClick={() => {
                              if (!isFuture && isCurrentMonth) {
                                setPlayedAt(day);
                                setCalendarMonth(day.startOf('month'));
                                setCalendarOpen(false);
                              }
                            }}
                            className={`rounded-full p-1.5 text-sm transition-colors ${
                              isSelected
                                ? 'bg-brand-royal font-bold text-white'
                                : isToday
                                  ? 'border border-brand-royal/50 text-brand-ice'
                                  : isFuture || !isCurrentMonth
                                    ? 'cursor-not-allowed text-brand-ice/20'
                                    : 'text-brand-ice hover:bg-surface-muted'
                            }`}
                          >
                            {day.date()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            {playedAt && playedAt.isAfter(dayjs(), 'day') && (
              <p className="text-xs text-loss">La data non può essere nel futuro</p>
            )}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">Tipo di partita</p>
          </div>
          <div className="grid gap-3">
            {[MATCH_TYPES.CLASSIC, MATCH_TYPES.KING, MATCH_TYPES.SUPER_KING].map((type) => {
              const isSelected = matchType === type;
              const icon = type === MATCH_TYPES.CLASSIC ? '🎾' : type === MATCH_TYPES.KING ? '👑' : '🔀';
              const title = type === MATCH_TYPES.CLASSIC ? 'Partita Classica' : type === MATCH_TYPES.KING ? 'King of the Court' : 'Americano';
              const subtitle = type === MATCH_TYPES.CLASSIC ? '2v2 con squadre fisse' : type === MATCH_TYPES.KING ? 'Rotazione coppie ogni round' : 'Tutti giocano con tutti';
              const playersLabel = type === MATCH_TYPES.CLASSIC ? '4 giocatori' : type === MATCH_TYPES.KING ? '4-8 giocatori' : '4-16 giocatori';
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMatchType(type)}
                  className={`relative rounded-card border p-4 text-left transition-colors ${isSelected ? 'border-brand-royal bg-brand-royal/10' : 'border-surface-muted bg-surface-card'}`}
                >
                  {isSelected && <span className="absolute right-3 top-3 h-3 w-3 rounded-full bg-win" />}
                  <div className="space-y-2">
                    <p className="text-lg">{icon}</p>
                    <p className="font-display text-lg font-bold text-brand-ice">{title}</p>
                    <p className="text-sm text-brand-ice/70">{subtitle}</p>
                    {type === MATCH_TYPES.CLASSIC && (
                      <p className="text-xs font-semibold text-win">🏆 La squadra vincente guadagna punti ELO</p>
                    )}
                    <span className="inline-flex rounded-badge border border-brand-sky/40 bg-brand-sky/10 px-2.5 py-0.5 text-xs font-semibold text-brand-sky">
                      {playersLabel}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            disabled={!canAdvanceStep1}
            onClick={() => setActiveStep(1)}
            className={`w-full ${canAdvanceStep1 ? 'bg-win text-brand-navy hover:bg-win/90' : 'bg-surface-muted text-brand-ice/60 hover:bg-surface-muted'}`}
          >
            ⚡ Continua
          </Button>
        </div>
      )}

      {/* STEP 2 */}
      {activeStep === 1 && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-4 p-4">
            {matchType === MATCH_TYPES.CLASSIC ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">GIOCATORI · {classicSelected.length}/4 SELEZIONATI</p>
                    <div className="mt-1 flex items-center gap-2 text-sm text-brand-ice/80">
                      <span>{classicSubtitle}</span>
                      {classicReady && <span className="rounded-badge bg-win/20 px-2 py-0.5 text-xs font-semibold text-win">● PRONTI</span>}
                    </div>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/groups/${groupId}`)}>
                    + Aggiungi Ospite
                  </Button>
                </div>
                <div className="space-y-2">
                  {members
                    .filter((m) => getPlayerId(m))
                    .map((m) => {
                      const playerId = getPlayerId(m);
                      const guest = m[GROUP_MEMBERS_FIELDS.IS_GUEST];
                      const team = getClassicTeam(playerId);
                      const selected = !!team;
                      const displayName = getDisplayNameForPlayer(playerId);
                      const rating = playerRatingById(playerId);
                      return (
                        <motion.button
                          key={playerId}
                          type="button"
                          onClick={() => togglePlayerForMatch(playerId)}
                          whileTap={{ scale: 0.99 }}
                          animate={{ scale: selected ? 1.02 : 1 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          className={`flex w-full items-center justify-between rounded-card border px-3 py-2 text-left transition-colors duration-150 ${team === 'A' ? 'border-win/60 bg-win/10' : team === 'B' ? 'border-brand-sky/60 bg-brand-sky/10' : 'border-surface-muted bg-surface'}`}
                        >
                          <span className="flex items-center gap-3">
                            <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${guest ? 'bg-win/25 text-win' : getHashColor(playerId)}`}>
                              {getInitials(displayName)}
                            </span>
                            <span>
                              <span className="flex items-center gap-2 font-medium text-brand-ice">
                                {displayName}
                                <AnimatePresence>
                                  {team && (
                                    <motion.span
                                      key={`${playerId}-${team}`}
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.8 }}
                                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                      className={team === 'A' ? 'bg-win text-black text-xs font-bold px-1.5 rounded' : 'bg-brand-sky text-black text-xs font-bold px-1.5 rounded'}
                                    >
                                      {team}
                                    </motion.span>
                                  )}
                                </AnimatePresence>
                              </span>
                              <span className="block text-xs text-brand-ice/55">{rating} pts</span>
                            </span>
                          </span>
                          <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${team === 'A' ? 'border-win bg-win text-brand-navy' : team === 'B' ? 'border-brand-sky bg-brand-sky text-brand-navy' : 'border-surface-muted text-transparent'}`}>
                            ✓
                          </span>
                        </motion.button>
                      );
                    })}
                </div>
                <AnimatePresence>
                  {classicSelected.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      className="space-y-3 rounded-card border border-surface-muted bg-surface p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">
                            SQUADRE {classicReady && <span className="ml-2 rounded-badge bg-win/20 px-2 py-0.5 text-xs font-semibold text-win">● PRONTI</span>}
                          </p>
                          {classicHint && <p className="mt-1 text-xs text-brand-ice/60">Tocca un giocatore per spostarlo tra le squadre</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
                        <div className="space-y-2 rounded-card border border-win/60 bg-win/10 p-2">
                          <p className="text-xs font-semibold uppercase text-win">SQUADRA A</p>
                          <p className="text-xs text-brand-ice/70">Avg {avgA} pts</p>
                          {classicPairA.map((pid) => (
                            <motion.div
                              key={pid}
                              layout
                              layoutId={`player-${pid}`}
                              animate={autoBalancePulse > 0 ? { x: [0, -4, 4, 0] } : undefined}
                              transition={{ duration: 0.3, ease: 'easeOut' }}
                              className="flex items-center justify-between rounded-badge border border-win/40 bg-surface px-2 py-1"
                            >
                              <span className="flex items-center gap-2">
                                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${getHashColor(pid)}`}>{getInitials(getDisplayNameForPlayer(pid))}</span>
                                <span className="text-sm text-brand-ice">{getDisplayNameForPlayer(pid)}</span>
                              </span>
                              <span className="flex items-center gap-2">
                                <span className="text-xs text-brand-ice/65">{playerRatingById(pid)} pts</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSwitchPlayer(pid); }}
                                  className="ml-2 text-brand-ice/40 hover:text-brand-ice transition-colors"
                                  aria-label="Sposta squadra"
                                >
                                  ⇄
                                </button>
                              </span>
                            </motion.div>
                          ))}
                        </div>
                        <div className="self-center px-2 pt-4 text-center text-brand-ice/60">VS</div>
                        <div className="space-y-2 rounded-card border border-brand-sky/60 bg-brand-sky/10 p-2">
                          <p className="text-xs font-semibold uppercase text-brand-sky">SQUADRA B</p>
                          <p className="text-xs text-brand-ice/70">Avg {avgB} pts</p>
                          {classicPairB.map((pid) => (
                            <motion.div
                              key={pid}
                              layout
                              layoutId={`player-${pid}`}
                              animate={autoBalancePulse > 0 ? { x: [0, -4, 4, 0] } : undefined}
                              transition={{ duration: 0.3, ease: 'easeOut' }}
                              className="flex items-center justify-between rounded-badge border border-brand-sky/40 bg-surface px-2 py-1"
                            >
                              <span className="flex items-center gap-2">
                                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${getHashColor(pid)}`}>{getInitials(getDisplayNameForPlayer(pid))}</span>
                                <span className="text-sm text-brand-ice">{getDisplayNameForPlayer(pid)}</span>
                              </span>
                              <span className="flex items-center gap-2">
                                <span className="text-xs text-brand-ice/65">{playerRatingById(pid)} pts</span>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleSwitchPlayer(pid); }}
                                  className="ml-2 text-brand-ice/40 hover:text-brand-ice transition-colors"
                                  aria-label="Sposta squadra"
                                >
                                  ⇄
                                </button>
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                      {classicReady && (
                        <>
                          {classicBalanceState === 'balanced' ? (
                            <div className="mt-3 flex items-center justify-center gap-2 rounded-card border border-win/30 bg-win/10 px-4 py-3">
                              <span className="text-lg">⚖️</span>
                              <div className="text-center">
                                <p className="text-sm font-semibold text-win">Squadre bilanciate</p>
                                <p className="text-xs text-win/70">Differenza: {classicGap} pts</p>
                              </div>
                            </div>
                          ) : classicBalanceState === 'slightly_unbalanced' ? (
                            <div className="mt-3 flex items-center justify-between gap-2 rounded-card border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">⚖️</span>
                                <div>
                                  <p className="text-sm font-semibold text-amber-400">Leggermente sbilanciato</p>
                                  <p className="text-xs text-amber-400/70">Differenza: {classicGap} pts</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={autoBalanceClassicTeams}
                                className="shrink-0 rounded-btn border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-400 transition-colors hover:bg-amber-500/20"
                              >
                                Bilancia
                              </button>
                            </div>
                          ) : (
                            <div className="mt-3 flex items-center justify-between gap-2 rounded-card border border-loss/30 bg-loss/10 px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">⚠️</span>
                                <div>
                                  <p className="text-sm font-semibold text-loss">Squadre sbilanciate</p>
                                  <p className="text-xs text-loss/70">Differenza: {classicGap} pts</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={autoBalanceClassicTeams}
                                className="shrink-0 rounded-btn border border-loss/40 bg-loss/10 px-3 py-1.5 text-xs font-semibold text-loss transition-colors hover:bg-loss/20"
                              >
                                Bilancia
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">Giocatori</p>
                    <p className="text-sm text-brand-ice/80">{selectedPlayers.length}/{requiredPlayers} · Seleziona {requiredPlayers} giocatori (richiesti)</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/groups/${groupId}`)}>
                    Aggiungi Ospite
                  </Button>
                </div>
                <div className="space-y-2">
                  {members
                    .filter((m) => getPlayerId(m))
                    .map((m) => {
                      const playerId = getPlayerId(m);
                      const guest = m[GROUP_MEMBERS_FIELDS.IS_GUEST];
                      const selected = selectedPlayers.includes(playerId);
                      const displayName = getDisplayNameForPlayer(playerId);
                      const rating = playerRatingById(playerId);
                      return (
                        <button
                          key={playerId}
                          type="button"
                          onClick={() => togglePlayer(playerId)}
                          className={`flex w-full items-center justify-between rounded-card border px-3 py-2 text-left ${selected ? 'border-brand-royal bg-brand-royal/10' : 'border-surface-muted bg-surface'}`}
                        >
                          <span className="flex items-center gap-3">
                            <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${guest ? 'bg-win/25 text-win' : getHashColor(playerId)}`}>
                              {getInitials(displayName)}
                            </span>
                            <span>
                              <span className="block font-medium text-brand-ice">{displayName}</span>
                              <span className="block text-xs text-brand-ice/55">{rating} pts</span>
                            </span>
                          </span>
                          <span className={`flex h-6 w-6 items-center justify-center rounded-full border ${selected ? 'border-win bg-win text-brand-navy' : 'border-surface-muted text-transparent'}`}>
                            ●
                          </span>
                        </button>
                      );
                    })}
                </div>
                <Button
                  type="button"
                  disabled={!canAdvanceStep2}
                  onClick={() => setActiveStep(2)}
                  className={`w-full ${canAdvanceStep2 ? 'bg-win text-brand-navy hover:bg-win/90' : 'bg-surface-muted text-brand-ice/60 hover:bg-surface-muted'}`}
                >
                  ⚡ {canAdvanceStep2 ? 'Continua alla formazione squadre' : `Seleziona ${requiredPlayers} giocatori per iniziare`}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && matchType === MATCH_TYPES.KING && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-3 p-4">
            <Button type="button" variant="outline" onClick={regenerateKing} className="w-fit">
              <IconShuffle className="mr-2 h-4 w-4" />
              Rigenera
            </Button>
          {kingOrder.length === 4 && (
            <>
              {buildKingPairs(kingOrder).map((p, i) => (
                <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                  <p className="text-sm text-brand-ice">
                    Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])} + {getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])} + {getDisplayNameForPlayer(p.team2[1])}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={0} value={kingSetScores[i]?.score1 ?? 0} onChange={(e) => setKingSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={kingSetScores[i]?.score2 ?? 0} onChange={(e) => setKingSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
                </div>
              ))}
            </>
          )}
          </CardContent>
        </Card>
      )}

      {activeStep === 2 && matchType === MATCH_TYPES.SUPER_KING && (
        <Card className="border-surface-muted bg-surface-card">
          <CardContent className="space-y-4 p-4">
            <p className="text-sm text-brand-ice/80">Divisione campi (serpentina per rating)</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <h3 className="font-display text-base font-semibold text-gold">Campo Gold</h3>
              {superGoldField.map((uid, idx) => (
                  <div key={uid} className="flex items-center justify-between rounded-card border border-surface-muted bg-surface px-3 py-2">
                    <span className="rounded-badge border border-gold/30 bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold">{getDisplayNameForPlayer(uid)}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => moveBetweenFields(true, idx)}>→ Silver</Button>
                  </div>
              ))}
              </div>
              <div className="space-y-2">
                <h3 className="font-display text-base font-semibold text-brand-ice/70">Campo Silver</h3>
              {superSilverField.map((uid, idx) => (
                  <div key={uid} className="flex items-center justify-between rounded-card border border-surface-muted bg-surface px-3 py-2">
                    <span className="rounded-badge border border-brand-sky/30 bg-brand-sky/10 px-2 py-0.5 text-xs font-semibold text-brand-sky">{getDisplayNameForPlayer(uid)}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => moveBetweenFields(false, idx)}>→ Gold</Button>
                  </div>
              ))}
              </div>
            </div>
          {superGoldOrder.length === 4 && superSilverOrder.length === 4 && (
            <>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-gold">King Campo Gold</h3>
                    <Button type="button" variant="outline" size="sm" onClick={regenerateSuperGold}>
                      <IconShuffle className="mr-1 h-4 w-4" />
                      Rigenera
                    </Button>
                  </div>
              {buildKingPairs(superGoldOrder).map((p, i) => (
                    <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                      <p className="text-sm text-brand-ice/80">Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])}+{getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])}+{getDisplayNameForPlayer(p.team2[1])}</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={superGoldSetScores[i]?.score1 ?? 0} onChange={(e) => setSuperGoldSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                        <span className="text-brand-ice/70">-</span>
                        <Input type="number" min={0} value={superGoldSetScores[i]?.score2 ?? 0} onChange={(e) => setSuperGoldSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                      </div>
                    </div>
              ))}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold text-brand-ice/70">King Campo Silver</h3>
                    <Button type="button" variant="outline" size="sm" onClick={regenerateSuperSilver}>
                      <IconShuffle className="mr-1 h-4 w-4" />
                      Rigenera
                    </Button>
                  </div>
              {buildKingPairs(superSilverOrder).map((p, i) => (
                    <div key={i} className="space-y-1 rounded-card border border-surface-muted bg-surface px-3 py-2">
                      <p className="text-sm text-brand-ice/80">Set {i + 1}: {getDisplayNameForPlayer(p.team1[0])}+{getDisplayNameForPlayer(p.team1[1])} vs {getDisplayNameForPlayer(p.team2[0])}+{getDisplayNameForPlayer(p.team2[1])}</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={superSilverSetScores[i]?.score1 ?? 0} onChange={(e) => setSuperSilverSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                        <span className="text-brand-ice/70">-</span>
                        <Input type="number" min={0} value={superSilverSetScores[i]?.score2 ?? 0} onChange={(e) => setSuperSilverSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                      </div>
                    </div>
              ))}
                </div>
              {/* Substep C — Calcolo automatico finalisti (solo lettura) */}
              {superKingGoldStandings.length === 4 && superKingSilverStandings.length === 4 && (
                  <div className="space-y-2 rounded-card border border-gold/30 bg-gold/10 p-4">
                    <p className="font-display text-base font-semibold text-gold">Calcolo finalisti (automatico)</p>
                    <p className="text-sm text-brand-ice/70">Classifica per set vinti, a parità per game vinti.</p>
                    <p className="text-sm font-semibold text-brand-ice">Classifica Campo Gold</p>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-brand-ice/80">
                    {superKingGoldStandings.map((row, idx) => (
                        <li key={row.playerId}>
                          {idx + 1}° {getDisplayNameForPlayer(row.playerId)} ({row.setWins} set, {row.gamesWon} game)
                        </li>
                    ))}
                    </ol>
                    <p className="text-sm font-semibold text-brand-ice">Classifica Campo Silver</p>
                    <ol className="list-decimal space-y-1 pl-5 text-sm text-brand-ice/80">
                    {superKingSilverStandings.map((row, idx) => (
                        <li key={row.playerId}>
                          {idx + 1}° {getDisplayNameForPlayer(row.playerId)} ({row.setWins} set, {row.gamesWon} game)
                        </li>
                    ))}
                    </ol>
                    <p className="text-sm font-semibold text-gold">King Gold</p>
                    <p className="text-sm text-brand-ice/80">
                    {getDisplayNameForPlayer(superKingGoldStandings[0].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[0].playerId)} vs {getDisplayNameForPlayer(superKingGoldStandings[1].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[1].playerId)}
                    </p>
                    <p className="text-sm font-semibold text-brand-ice/70">King Silver</p>
                    <p className="text-sm text-brand-ice/80">
                    {getDisplayNameForPlayer(superKingGoldStandings[2].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[2].playerId)} vs {getDisplayNameForPlayer(superKingGoldStandings[3].playerId)} + {getDisplayNameForPlayer(superKingSilverStandings[3].playerId)}
                    </p>
                  </div>
              )}
                <p className="text-sm font-semibold text-gold">Substep D — Punteggi finali. Finale King Gold</p>
              {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-sm text-brand-ice/80">Set {i + 1}</span>
                    <Input type="number" min={0} value={superGoldFinalScores[i]?.score1 ?? 0} onChange={(e) => setSuperGoldFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={superGoldFinalScores[i]?.score2 ?? 0} onChange={(e) => setSuperGoldFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
              ))}
                <p className="text-sm font-semibold text-brand-ice/70">Finale King Silver</p>
              {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-sm text-brand-ice/80">Set {i + 1}</span>
                    <Input type="number" min={0} value={superSilverFinalScores[i]?.score1 ?? 0} onChange={(e) => setSuperSilverFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className="w-20" />
                    <span className="text-brand-ice/70">-</span>
                    <Input type="number" min={0} value={superSilverFinalScores[i]?.score2 ?? 0} onChange={(e) => setSuperSilverFinalScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className="w-20" />
                  </div>
              ))}
            </>
          )}
          </CardContent>
        </Card>
      )}

      {/* STEP 3/4 */}
      {((matchType === MATCH_TYPES.CLASSIC && activeStep === 2) || (matchType !== MATCH_TYPES.CLASSIC && activeStep === 3)) && (
        <>
          {matchType === MATCH_TYPES.CLASSIC ? (
            <div className="space-y-4">
              <Card className="border-brand-royal/40 bg-brand-royal/10">
                <CardContent className="space-y-4 p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand-ice/70">
                    <IconTrophy className="h-4 w-4 text-gold" />
                    Partita classica
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                    <div className="space-y-1">
                      <p className="text-xs font-bold uppercase tracking-[0.1em] text-brand-sky">Squadra A</p>
                      <div className="flex -space-x-2">
                        {classicPairA.map((pid) => (
                          <span key={pid} className={`flex h-8 w-8 items-center justify-center rounded-full border border-surface-card text-xs font-bold ${getHashColor(pid)}`}>
                            {getInitials(getDisplayNameForPlayer(pid))}
                          </span>
                        ))}
                      </div>
                      {classicPairA.map((pid) => <p key={pid} className="text-sm text-brand-ice">{getDisplayNameForPlayer(pid)}</p>)}
                      <p className="text-xs text-brand-ice/55">MEDIA {avgA} PT</p>
                    </div>
                    <div className="flex h-full items-center justify-center border-x border-surface-muted px-3">
                      <p className="font-display text-lg font-bold text-brand-ice/60">VS</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-xs font-bold uppercase tracking-[0.1em] text-win">Squadra B</p>
                      <div className="flex justify-end -space-x-2">
                        {classicPairB.map((pid) => (
                          <span key={pid} className={`flex h-8 w-8 items-center justify-center rounded-full border border-surface-card text-xs font-bold ${getHashColor(pid)}`}>
                            {getInitials(getDisplayNameForPlayer(pid))}
                          </span>
                        ))}
                      </div>
                      {classicPairB.map((pid) => <p key={pid} className="text-sm text-brand-ice">{getDisplayNameForPlayer(pid)}</p>)}
                      <p className="text-xs text-brand-ice/55">MEDIA {avgB} PT</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-surface-muted bg-surface-card">
                <CardContent className="space-y-3 p-4">
                  <p className="font-display text-xl font-bold text-brand-ice">Set</p>
                  {classicSetScores.slice(0, classicNumSets).map((s, i) => {
                    const s1 = Number(s.score1) || 0;
                    const s2 = Number(s.score2) || 0;
                    const winA = s1 > s2;
                    const winB = s2 > s1;
                    const isLastSet = i === classicNumSets - 1;
                    return (
                      <div key={i}>
                        <div className="rounded-card border border-surface-muted bg-surface p-3">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-ice/60">Set {i + 1}</p>
                            <span className={`rounded-badge px-2 py-0.5 text-xs font-semibold ${winA ? 'bg-win/20 text-win' : winB ? 'bg-brand-sky/20 text-brand-sky' : 'bg-surface-muted text-brand-ice/70'}`}>
                              {winA ? 'Vince Squadra A' : winB ? 'Vince Squadra B' : 'Parità'}
                            </span>
                          </div>
                          <div className="flex items-center justify-center gap-3">
                            <Input type="number" min={0} value={s.score1} onChange={(e) => setClassicSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score1: e.target.value }; return n; })} className={`h-14 w-24 text-center font-display text-3xl font-bold ${winA ? 'border-win/60' : ''}`} />
                            <span className="font-display text-2xl text-brand-ice/70">-</span>
                            <Input type="number" min={0} value={s.score2} onChange={(e) => setClassicSetScores((prev) => { const n = [...prev]; n[i] = { ...(prev[i] || {}), score2: e.target.value }; return n; })} className={`h-14 w-24 text-center font-display text-3xl font-bold ${winB ? 'border-win/60' : ''}`} />
                          </div>
                          {isLastSet && classicNumSets > 1 && (
                            <div className="flex justify-end pt-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setClassicNumSets((n) => n - 1);
                                  setClassicSetScores((prev) => prev.slice(0, -1));
                                }}
                                className="flex items-center gap-1 text-xs text-brand-ice/30 transition-colors hover:text-loss"
                                aria-label="Rimuovi set"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
                                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Rimuovi
                              </button>
                            </div>
                          )}
                        </div>
                        {isLastSet && (
                          <div className="mt-2 rounded-card border border-surface-muted bg-surface-card p-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">Inserimento rapido</p>
                            <div className="grid grid-cols-2 gap-2">
                              {QUICK_SCORES.map(({ s1, s2 }) => (
                                <React.Fragment key={`${s1}-${s2}`}>
                                  <button
                                    type="button"
                                    onClick={() => applyQuickScore(s1, s2)}
                                    className="rounded-btn border border-surface-muted bg-surface px-3 py-2 text-sm font-semibold text-brand-ice transition-colors hover:border-win/60 hover:bg-win/10"
                                  >
                                    {s1}-{s2}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => applyQuickScore(s2, s1)}
                                    className="rounded-btn border border-surface-muted bg-surface px-3 py-2 text-sm font-semibold text-brand-ice transition-colors hover:border-brand-sky/60 hover:bg-brand-sky/10"
                                  >
                                    {s2}-{s1}
                                  </button>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setClassicNumSets((n) => n + 1)}
                    className="w-full rounded-btn border border-dashed border-win/70 px-3 py-2 text-sm font-semibold text-win"
                  >
                    + Aggiungi set
                  </button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDuplicateLastSet}
                    className="w-full"
                  >
                    Duplica ultimo
                  </Button>
                  <Button type="button" onClick={handleCalculateResult} className="w-full bg-brand-royal hover:bg-brand-royal/90">
                    Calcola risultato
                  </Button>
                </CardContent>
              </Card>

              {matchResultCalculated && eloPreview && (
                <Card className="border-win/20 bg-win/10">
                  <CardContent className="space-y-3 p-4">
                    <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-win/25">
                      <IconTrophy className="h-5 w-5 text-win" />
                    </div>
                    <p className="text-center text-xs font-semibold uppercase tracking-[0.15em] text-brand-ice/60">Risultato partita</p>
                    <p className="text-center font-display text-2xl font-bold text-win">
                      {eloPreview.winner === 'A' ? 'Vince Squadra A' : eloPreview.winner === 'B' ? 'Vince Squadra B' : 'Pareggio'}
                    </p>
                    <p className="text-center text-sm text-brand-ice/75">{eloPreview.setsWonA} vittorie A · {eloPreview.setsWonB} vittorie B</p>
                    <div className="h-px bg-win/20" />
                    {ratingImpact === RATING_IMPACT.EXCLUDED ? (
                      <p className="text-center text-sm text-brand-ice/70">Partita non ufficiale · nessun impatto sul ranking</p>
                    ) : (
                      <div className="grid grid-cols-3 items-center text-center">
                        <p className={`font-display text-xl font-bold ${(eloPreview.deltaA || 0) >= 0 ? 'text-win' : 'text-loss'}`}>{eloPreview.deltaA == null ? '-' : `${eloPreview.deltaA > 0 ? '+' : ''}${eloPreview.deltaA}`}</p>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-ice/50">ELO</p>
                        <p className={`font-display text-xl font-bold ${(eloPreview.deltaB || 0) >= 0 ? 'text-win' : 'text-loss'}`}>{eloPreview.deltaB == null ? '-' : `${eloPreview.deltaB > 0 ? '+' : ''}${eloPreview.deltaB}`}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between rounded-card border border-surface-muted bg-surface p-3">
                <div>
                  <p className="text-sm font-medium text-brand-ice">Conta per il ranking stagionale</p>
                  <p className="text-xs text-brand-ice/50">Disattiva se la partita include ospiti o non è rappresentativa</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={ratingImpact === RATING_IMPACT.OFFICIAL}
                  onClick={() => setRatingImpact((prev) => (prev === RATING_IMPACT.OFFICIAL ? RATING_IMPACT.EXCLUDED : RATING_IMPACT.OFFICIAL))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${ratingImpact === RATING_IMPACT.OFFICIAL ? 'bg-win' : 'bg-surface-muted'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${ratingImpact === RATING_IMPACT.OFFICIAL ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {matchResultCalculated && (
                <Button type="button" onClick={handleSubmit} disabled={submitLoading} className="w-full bg-win text-brand-navy hover:bg-win/90">
                  {submitLoading ? 'Invio in corso...' : '💾 Salva risultato'}
                </Button>
              )}
            </div>
          ) : (
            <Card className="border-surface-muted bg-surface-card">
              <CardContent className="space-y-3 p-4">
                <h2 className="font-display text-lg font-semibold text-brand-ice">Riepilogo</h2>
                <p className="text-sm text-brand-ice/80">Tipo partita: {TYPE_CONFIG[matchType]?.label}</p>
                <p className="text-sm text-brand-ice/80">Giocatori: {selectedPlayers.map(getDisplayNameForPlayer).join(', ')}</p>
                <p className="text-sm text-brand-ice/80">Numero set: {buildSetsPayload().length}</p>
                <div className="flex items-center justify-between rounded-card border border-surface-muted bg-surface p-3">
                  <div>
                    <p className="text-sm font-medium text-brand-ice">Conta per il ranking</p>
                    <p className="text-xs text-brand-ice/50">
                      Disattiva se la partita include ospiti o non è rappresentativa
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={ratingImpact === RATING_IMPACT.OFFICIAL}
                    onClick={() => setRatingImpact((prev) =>
                      prev === RATING_IMPACT.OFFICIAL ? RATING_IMPACT.EXCLUDED : RATING_IMPACT.OFFICIAL
                    )}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      ratingImpact === RATING_IMPACT.OFFICIAL ? 'bg-brand-royal' : 'bg-surface-muted'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                        ratingImpact === RATING_IMPACT.OFFICIAL ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <Button type="button" onClick={handleSubmit} disabled={submitLoading} className="w-full">
                {submitLoading ? 'Invio in corso...' : 'Invia per approvazione'}
              </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <div className="mt-3 flex items-center justify-between">
        <Button type="button" variant="outline" disabled={activeStep === 0 || submitLoading} onClick={() => setActiveStep((s) => s - 1)}>
          Indietro
        </Button>
        {matchType === MATCH_TYPES.CLASSIC && activeStep === 1 ? (
          <motion.button
            type="button"
            disabled={!classicReady || submitLoading}
            animate={{ opacity: classicReady ? 1 : 0.5, scale: classicReady ? 1 : 0.98 }}
            transition={{ type: 'spring', stiffness: 300 }}
            onClick={() => setActiveStep(2)}
            className={`rounded-btn px-4 py-2 font-semibold ${classicReady ? 'bg-win text-brand-navy hover:bg-win/90' : 'bg-surface-muted text-brand-ice/60'}`}
          >
            {classicReady ? '⚡ Continua ai punteggi' : '⚡ Seleziona 4 giocatori'}
          </motion.button>
        ) : (
          <Button
            type="button"
            disabled={(activeStep === 0 && !canAdvanceStep1) || (activeStep === 1 && !canAdvanceStep2) || (activeStep === 2 && !canGoToClassicScores) || (activeStep === steps.length - 1 && matchType === MATCH_TYPES.CLASSIC) || submitLoading}
            onClick={() => {
              if (activeStep === steps.length - 1 && matchType !== MATCH_TYPES.CLASSIC) {
                handleSubmit();
                return;
              }
              setActiveStep((s) => s + 1);
            }}
          >
            {activeStep === steps.length - 1 ? (matchType === MATCH_TYPES.CLASSIC ? 'Calcola e salva in questa schermata' : (submitLoading ? 'Invio in corso...' : 'Invia')) : 'Avanti'}
          </Button>
        )}
      </div>
    </div>
  );
}
